import {closeModal, openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import type {
  CheckoutRequest,
  CreditPackCheckoutOptions,
  CreditPackCheckoutRequest,
  DownloadCheckoutOptions,
  DownloadCheckoutRequest,
  SubscriptionCheckoutOptions,
  SubscriptionCheckoutRequest,
} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutMode} from '@Components/checkout-flow/checkout-flow.types';
import {startCheckoutForEligibleBuyer} from '@Components/checkout-flow/credit-pack-eligibility.library';
import {isModernCheckoutEnabled} from '@Components/checkout-flow/checkout-flow.library';
import {assertNever} from '@Utils/assert-never.util';
import {noop} from '@Utils/general.util';

const LEGACY_CHECKOUT_BUNDLE_NAME = 'stripeCheckoutDialog';

export const openCheckoutModal = (request: CheckoutRequest): void => {
  void startCheckoutForEligibleBuyer(request, dispatchOpenCheckoutModal);
};

const dispatchOpenCheckoutModal = (request: CheckoutRequest): void => {
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.CHECKOUT_MODAL,
      panelProps: {request},
    })
  );
};

export const closeCheckoutModal = (): void => {
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.CHECKOUT_MODAL));
};

/**
 * React callers go through here so the modern/classic kill-switch governs every entry point: with the flag
 * off the request is replayed against the legacy jQuery dialog exactly as before.
 */
export const openCheckout = (request: CheckoutRequest): void => {
  if (isModernCheckoutEnabled()) {
    openCheckoutModal(request);
    return;
  }
  window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', LEGACY_CHECKOUT_BUNDLE_NAME, true, () => {
    openLegacyCheckout(request);
  });
};

export const openCheckoutForSubscription = (options: SubscriptionCheckoutOptions): void => {
  openCheckout(subscriptionCheckoutRequest(options));
};

export const openCheckoutForCreditPackPurchase = (options: CreditPackCheckoutOptions): void => {
  openCheckout(creditPackCheckoutRequest(options));
};

export const openCheckoutForDownloadPurchase = (options: DownloadCheckoutOptions): void => {
  openCheckout(downloadCheckoutRequest(options));
};

/**
 * Surfaces that render their own CheckoutHostProvider open the checkout through `useOpenCheckout` and so
 * need the request itself rather than an opener.
 */
export const subscriptionCheckoutRequest = (options: SubscriptionCheckoutOptions): SubscriptionCheckoutRequest => {
  return {...options, mode: CheckoutMode.SUBSCRIPTION};
};

export const creditPackCheckoutRequest = (options: CreditPackCheckoutOptions): CreditPackCheckoutRequest => {
  return {...options, mode: CheckoutMode.CREDIT_PACK};
};

export const downloadCheckoutRequest = (options: DownloadCheckoutOptions): DownloadCheckoutRequest => {
  return {...options, mode: CheckoutMode.DOWNLOAD};
};

/**
 * @deprecated Replays the request against the legacy jQuery dialog. Reached only while
 * `isModernCheckoutEnabled()` is off; delete this and `stripe-checkout.js` with the flag.
 */
const openLegacyCheckout = (request: CheckoutRequest): void => {
  const legacyCheckout = window.PMW.stripeCheckout;
  switch (request.mode) {
    case CheckoutMode.SUBSCRIPTION:
      if (request.isTrial) {
        legacyCheckout.showStripeCheckoutForNewTrial(request.priceId, '', request.posterHashedId, request.onOpen ?? noop, request.trialSource);
        return;
      }
      legacyCheckout.openStripeCheckoutForSubscription(request.priceId, request.seats, request.price, request.onClose ?? noop, request.promoCode, request.onOpen ?? noop, request.isResubscribe);
      return;
    case CheckoutMode.CREDIT_PACK:
      legacyCheckout.showStripeCheckoutForCreditPackPurchase(request.onSuccess ?? noop, request.onClose ?? noop, request.headingText, request.modalTitle, request.onOpen ?? noop);
      return;
    case CheckoutMode.DOWNLOAD:
      legacyCheckout.showStripeCheckoutForDownloadPurchase(request.priceId, request.cartId, request.posterHashedId, request.productId, request.isSilent, request.onSuccess ?? noop, request.onClose ?? noop);
      return;
    case CheckoutMode.PREMIUM_CONFIRMATION:
      legacyCheckout.showPremiumConfirmationPanel(request.ga4PurchaseEventParams ?? undefined, request.trackingData);
      return;
    case CheckoutMode.WELCOME_TO_TEAM:
      legacyCheckout.showWelcomeToTeamDialog();
      return;
    case CheckoutMode.PAYMENT_PROCESSING:
      legacyCheckout.showCheckoutForPaymentProcessing(request.sessionId);
      return;
    default:
      assertNever(request);
  }
};
